/**
 * Sanitizes a string to only include alphanumeric characters.
 * Removes all non-alphanumeric characters.
 *
 * @param str - The string to sanitize
 * @returns The sanitized string with only letters and numbers
 */
function sanitizeToAlphanumeric(str: string): string {
  return str.replace(/[^a-zA-Z0-9]/g, '');
}

/**
 * Ensures a string doesn't start with a number.
 * If it does, removes leading numbers.
 *
 * @param str - The string to check
 * @returns The string without leading numbers
 */
function removeLeadingNumbers(str: string): string {
  return str.replace(/^[0-9]+/, '');
}

/**
 * Generates a username prefix from an email address.
 *
 * Rules:
 * - Takes the local part of the email (before @)
 * - Removes all periods and concatenates all parts (e.g., john.paul.jones@domain -> JOHNPAULJONES_)
 * - Only alphanumeric characters are used (letters and numbers)
 * - Prefix cannot start with a number
 * - All output is uppercase
 *
 * @param email - The email address to generate prefix from
 * @returns The username prefix in uppercase with trailing underscore
 *
 * @example
 * generateUsernamePrefix('charlie.granger@example.com') // Returns 'CHARLIEGRANGER_'
 * generateUsernamePrefix('cgranger@example.com') // Returns 'CGRANGER_'
 * generateUsernamePrefix('john.doe@example.com') // Returns 'JOHNDOE_'
 * generateUsernamePrefix('john.paul.jones@example.com') // Returns 'JOHNPAULJONES_'
 * generateUsernamePrefix('john-paul.smith@example.com') // Returns 'JOHNPAULSMITH_'
 * generateUsernamePrefix('123test@example.com') // Returns 'TEST_'
 */
export function generateUsernamePrefix(email: string): string {
  if (!email?.includes('@')) {
    return '';
  }

  const localPart = email.split('@')[0];

  // Remove all non-alphanumeric characters (including periods)
  let prefix = sanitizeToAlphanumeric(localPart);

  // Remove leading numbers
  prefix = removeLeadingNumbers(prefix);

  return prefix ? prefix.toUpperCase() + '_' : '';
}

/**
 * Generates a default username for a Snowflake service user based on email.
 *
 * @param email - The email address to generate username from
 * @param suffix - The suffix to append (default: 'DATAOPS_SERVICE_USER')
 * @returns The complete username
 *
 * @example
 * generateDefaultUsername('charlie.granger@example.com') // Returns 'CHARLIEGRANGER_DATAOPS_SERVICE_USER'
 * generateDefaultUsername('cgranger@example.com') // Returns 'CGRANGER_DATAOPS_SERVICE_USER'
 */
export function generateDefaultUsername(email: string, suffix: string = 'DATAOPS_SERVICE_USER'): string {
  const prefix = generateUsernamePrefix(email);
  return prefix + suffix;
}
